/**
 * AP-28 4.1.1 — **Ein Preis nie ohne seine Bezugsmenge.**
 *
 * „1,24" an einem Meterartikel mit Preiseinheit 100 ist eine Falschaussage um
 * den Faktor 100. Liste, Maske und Export zeigen deshalb „1,24 / 100 m".
 */

import { geldText } from '@symphenia/shared-types'

/** „1,24 / 100 m" — bei Preiseinheit 1 nur der Betrag. Leer → „—". */
export function formatPreis(
  betrag: number | null | undefined,
  preiseinheit: number | null | undefined,
  einheit?: string | null,
): string {
  if (betrag === null || betrag === undefined || Number.isNaN(betrag)) return '—'
  const text = geldText(betrag)
  if (!preiseinheit || preiseinheit === 1) return text
  // Ungruppiert: Ein Gruppierungszeichen hinge am Gebietsschema des Browsers
  // („1.000", „1 000") — die Preiseinheit ist seit N-12 frei, aber meist klein.
  return `${text} / ${preiseinheit}${einheit ? ` ${einheit}` : ''}`
}

/** „20 %" bzw. „10,5 %"; leer → „—". */
export function formatSteuersatz(satz: number | null | undefined): string {
  if (satz === null || satz === undefined) return '—'
  return `${new Intl.NumberFormat('de-AT', { maximumFractionDigits: 2 }).format(satz)} %`
}

/** Gewicht in kg (N-7): „0,0042", „12,5"; leer → „—". */
export function formatGewicht(kg: number | null | undefined): string {
  if (kg === null || kg === undefined || Number.isNaN(kg)) return '—'
  return new Intl.NumberFormat('de-AT', { maximumFractionDigits: 4 }).format(kg)
}

/** „8,2 MB" — für den Hinweis nach dem Verkleinern eines Bildes. */
export function formatMegabyte(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  return `${new Intl.NumberFormat('de-AT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(mb)} MB`
}

/** Für ein Eingabefeld: 1.5 → „1,50". */
export function alsEingabe(betrag: number | null | undefined, stellen = 2): string {
  if (betrag === null || betrag === undefined) return ''
  return betrag.toFixed(stellen).replace('.', ',')
}

/**
 * Liest eine Dezimalzahl, wie sie getippt wird: Komma oder Punkt,
 * Tausenderpunkte werden entfernt. Leer → `null`, Unlesbares → `NaN`.
 */
export function leseDezimal(text: string): number | null {
  const roh = text.trim().replace(/\s/g, '')
  if (!roh) return null
  // „1.234,56" → Tausenderpunkt vor dem Komma
  const normal = roh.includes(',') ? roh.replace(/\./g, '').replace(',', '.') : roh
  if (!/^-?\d+(\.\d+)?$/.test(normal)) return Number.NaN
  return Number(normal)
}

/** Mehr als `stellen` Nachkommastellen? */
export function zuVieleStellen(zahl: number, stellen: number): boolean {
  return Math.round(zahl * 10 ** stellen) / 10 ** stellen !== zahl
}
